import { Component, ViewChild, OnInit, Input } from '@angular/core';
import { NotificationService, PagedResultGeneric, PagedSearch, TranslationService } from 'src/app/shared';
import { JournalEntryDetailsComponent } from '../journal-entry-details/journal-entry-details.component';
import { PostTransactionsSearchDto } from '../shared/dto/post-transactions-search.dto';
import { PostTransactionSummaryOptionsDto } from '../shared/dto/unposted-transaction-summary-options.dto';
import { UnpostedTransactionDto } from '../shared/dto/unposted-transaction-transaction.dto';
import { FinancialServiceService } from '../shared/financial.service';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { UserService } from 'src/app/users/shared/user.service';
import { BatchService } from '../shared/invoice/batch.service';
import { PostExistingTransactionDto } from '../shared/transaction/dto/post-existing-transaction.dto';
import { PostTransactionSummaryDto } from '../shared/dto/post-transaction-summary.dto';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { QuickBooksOnlineAuthorizationService } from 'src/app/integrations/quick-books-online/quick-books-online-authorization.service';
import { AccountingPackageService } from '../shared/accounting-package/accounting-package.service';
import { AccountingPackage } from '../shared/accounting-package/accounting-package.model';
import { IntacctService } from 'src/app/integrations/intacct/intacct.service';
import { IntacctJournalSearchDto } from 'src/app/integrations/intacct/dto/intacct-journal-search.dto';
import { IntacctJournalDto } from 'src/app/integrations/intacct/dto/intacct-journal.dto';
import { PostTransactionsRequestSearchComponent } from './post-transactions-request-search/post-transactions-request-search.component';
import { SecurityService } from 'src/app/admin/codes/shared/security.service';
import { PostTransactionSummaryAccountChangeDto } from '../shared/dto/unposted-transaction-summary-account-change.dto';
import { DebitCreditAccountValidationResultDto } from '../shared/dto/debit-credit-account.validation-result.dto';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'wl-post-transactions',
  templateUrl: './post-transactions.component.html',
  styleUrls: ['./post-transactions.component.less'],
  standalone: false,
})
export class PostTransactionsComponent implements OnInit {
  @ViewChild(JournalEntryDetailsComponent) journalEntryDetailsComponent: JournalEntryDetailsComponent;
  @ViewChild(PostTransactionsRequestSearchComponent) postTransactionSearchComponent: PostTransactionsRequestSearchComponent;
  currentYear: number;
  currentUserNum: number;
  postExistingTransactionDto = {} as PostExistingTransactionDto;
  isPosting: boolean = false;
  isSearching = false;
  searchQueryParams: PostTransactionsSearchDto = {} as PostTransactionsSearchDto;
  searchResults: PagedResultGeneric<UnpostedTransactionDto>;
  showEntryDetails = false;
  showSummary = false;
  transactionSummaries: Array<PostTransactionSummaryDto>;
  exportingSummaries = false;
  exportingDetails = false;
  nextBatchNumber: string;
  disablePostingButton: boolean = false;
  disablePostingConfirmation: boolean = false;
  intacctJournals: SelectItem[] = [];
  showIntacctFields = false;
  showRunBatchReport = false;
  displayWarning: boolean;
  readOnlyAccess: boolean = true;
  accountChanges: Array<PostTransactionSummaryAccountChangeDto> = [];
  updateJournalEntryLoading: boolean = false;
  constructor(
    private _confirmationService: ConfirmationService,
    private _translationService: TranslationService,
    private _notificationService: NotificationService,
    private _batchService: BatchService,
    private _userService: UserService,
    private _financialService: FinancialServiceService,
    private _navigationService: NavigationService,
    private _quickBooksOnlineAuthorizationService: QuickBooksOnlineAuthorizationService,
    private _accountingPackageService: AccountingPackageService,
    private _intacctService: IntacctService,
    private _securityService: SecurityService
  ) {}

  ngOnInit(): void {
    this._userService.getCurrentUser().then((user) => {
      this.currentUserNum = user?.UserNum ?? 0;
    });
    this._userService.getCurrentUserSecurityElementAccessLevel('Revenue_Module').then((access) => {
      if (access == 2) {
        this.readOnlyAccess = false;
      }
    });
    this.currentYear = new Date().getFullYear();

    let intacctJournalSearch = new PagedSearch() as IntacctJournalSearchDto;
    this._intacctService
      .searchIntacctJournals(intacctJournalSearch)
      .then((data) => {
        if (data.TotalCount > 0) {
          this.intacctJournals = data.Result.map<SelectItem>((x: IntacctJournalDto) => {
            return { label: x.Journal, value: x.IntacctJournalId };
          });
        }
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      });
    this.checkForValidPackage;
  }

  async viewDetailsClicked() {
    this.showEntryDetails = true;
    this.showRunBatchReport = false;
    this.journalEntryDetailsComponent.softReset();
    await this.journalEntryDetailsComponent.search();
  }

  getBatchNumber() {
    return this._batchService.getNextBatchNumber(this.currentYear).then((data: string) => {
      return data;
    });
  }

  previewNextBatchNumber() {
    this._batchService.getLastUsedBatchNumber(this.currentYear).then((nextBatchNumber: number) => {
      this.nextBatchNumber = `Posting Batch ${this.currentYear}-${nextBatchNumber + 1}`;
    });
  }

  async postTransactionsClicked() {
    //Validate if the accountingpackage is QBO
    let accountingPackage = await this._accountingPackageService.get(this.searchQueryParams.AccountingPackageID);

    if (accountingPackage.PackageType == 4 && accountingPackage.AutoSyncBatch) {
      let validConnection = await this._financialService.validateQuickBooksOnlineConnectiion(accountingPackage.AccountingPackageId);
      if (!validConnection) {
        this.disablePostingConfirmation = true;
        this.authorizeQuickBooksOnline(accountingPackage);
      }
    }

    this._confirmationService.confirm({
      message: this._translationService.getInstant('revenue.post-transactions.post-transactions-confirm-message'),
      key: 'postTransactionsCD',
      accept: async () => {
        this.postExistingTransactionDto.BatchPosting = await this.getBatchNumber();
        this.isPosting = true;
        this.postExistingTransactionDto.PostedBy = this.currentUserNum.toString();
        this.postExistingTransactionDto.SelectAll = this.journalEntryDetailsComponent.selectAll;
        this.postExistingTransactionDto.SelectedIds = [];

        // If SelectAll is true then set Selectedids to the non-checked transactions in order to have them filtered out of the results.
        this.journalEntryDetailsComponent.selectedMap.forEach((selected, id, map) => {
          if (this.postExistingTransactionDto.SelectAll && !selected) {
            this.postExistingTransactionDto.SelectedIds.push(id);
          } else if (!this.postExistingTransactionDto.SelectAll && selected) {
            this.postExistingTransactionDto.SelectedIds.push(id);
          }
        });

        this.postExistingTransactionDto.SearchParams = this.journalEntryDetailsComponent.searchParams;
        this.postExistingTransactionDto.AccountChanges = this.accountChanges;
        this._financialService
          .postExistingTransactions(this.postExistingTransactionDto)
          .then(() => {
            this.disablePostingButton = true;
            this.showRunBatchReport = true;
            this.showIntacctFields = false;
          })
          .catch((error) => {
            this._notificationService.httpError(error);
          })
          .finally(() => {
            this.isPosting = false;
            this.displayPostingTransactionsNotification();
          });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  async previewTransactionsClicked() {
    this.isSearching = true;
    this.disablePostingButton = true;
    this.previewNextBatchNumber();
    this.journalEntryDetailsComponent.init();
    let params: PostTransactionSummaryOptionsDto = this.searchQueryParams as PostTransactionSummaryOptionsDto;
    params.SelectAll = true;
    this.showSummary = true;
    params.AccountChanges = this.accountChanges;
    let validationResult = await this._financialService.validateUnpostedTransactions(this.searchQueryParams);
    this.showRunBatchReport = false;
    try {
      this.transactionSummaries = await this._financialService.summarizeTransactions(params);
    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        this._notificationService.httpError(error?.error?.message);
        this.isSearching = false;
        return;
      }
    }

    if (!validationResult.HasAccountingPackageMismatch && !validationResult.HasEmptyAccounts) {
      this.disablePostingButton = false;
    } else {
      this.displayMissingCreditDebitAccountWarnings(validationResult);
    }
    this.isSearching = false;
  }

  exportSummariesClicked() {
    this.exportingSummaries = true;
    this._financialService
      .exportTransactionSummaries(this.searchQueryParams)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .finally(() => {
        this.exportingSummaries = false;
      });
  }

  exportDetailsClicked() {
    this.exportingDetails = true;
    this._financialService
      .exportUnpostedTransactions(this.searchQueryParams)
      .then((csvUrl) => {
        this._navigationService.popupSafeNavigate(csvUrl);
      })
      .finally(() => {
        this.exportingDetails = false;
      });
  }

  runBatchReportClicked() {
    let startDate = new Date(Math.min(...this.transactionSummaries.map((x) => new Date(x.ApplyDate).getTime())));
    let endDate = new Date(Math.max(...this.transactionSummaries.map((x) => new Date(x.ApplyDate).getTime())));
    endDate.setDate(endDate.getDate() + 1);

    this._navigationService.navigateToRevenueDebitCreditTransactionsByBatchPostingReport(
      startDate,
      endDate,
      this.postExistingTransactionDto.BatchPosting
    );
  }

  async updateTransactionsClicked() {
    this.updateJournalEntryLoading = true;
    let params: PostTransactionSummaryOptionsDto = this.searchQueryParams as PostTransactionSummaryOptionsDto;
    params.AccountChanges = this.accountChanges;
    params.SelectAll = this.journalEntryDetailsComponent.selectAll;
    params.SelectedIds = [];
    // If SelectAll is true then set Selectedids to the non-checked transactions in order to have them filtered out of the results.
    this.journalEntryDetailsComponent.selectedMap.forEach((selected, id, map) => {
      if (params.SelectAll && !selected) {
        params.SelectedIds.push(id);
      } else if (!params.SelectAll && selected) {
        params.SelectedIds.push(id);
      }
    });

    let invalidTransactions = await this._financialService.validateUnpostedTransactions(this.searchQueryParams);

    if (!invalidTransactions.HasAccountingPackageMismatch && !invalidTransactions.HasEmptyAccounts) {
      this.transactionSummaries = await this._financialService.summarizeTransactions(params);
      this.disablePostingButton = false;
    } else {
      this.transactionSummaries = [];
      this.displayMissingCreditDebitAccountWarnings(invalidTransactions);
    }
    this.updateJournalEntryLoading = false;
    this.showEntryDetails = false;
    this.showSummary = true;
  }

  async authorizeQuickBooksOnline(accountingPackage: AccountingPackage) {
    //Wipe out the existing QBO data then repopulate them fresh.
    accountingPackage.QboToken = null;
    accountingPackage.QboCompanyId = null;
    await this._accountingPackageService.update(accountingPackage);

    this._quickBooksOnlineAuthorizationService.getAuthorizationUri(accountingPackage.AccountingPackageId).then((authData) => {
      this._quickBooksOnlineAuthorizationService.getAuthorizationCode(authData).then((data) => {
        accountingPackage.QboToken = data.Token;
        accountingPackage.QboCompanyId = data.CompanyId;
        accountingPackage.QboTokenSecret = '';
        accountingPackage.AcquiredDate = new Date();
        this._accountingPackageService.update(accountingPackage);
        this.disablePostingConfirmation = false;
      });
    });
  }

  transactionAccountChanged(acountChangeDto: PostTransactionSummaryAccountChangeDto) {
    let index = this.accountChanges.findIndex((x) => x.CompositeKey == acountChangeDto.CompositeKey);
    if (index == -1) {
      this.accountChanges.push(acountChangeDto);
    } else {
      this.accountChanges[index] = acountChangeDto;
    }
  }

  accountingPackageChanged(accountingPackageId: number) {
    this.accountChanges = [];
    this.checkForIntacctPackage(accountingPackageId);
  }

  checkForIntacctPackage(accountingPackageId: number) {
    this.accountChanges = [];
    this._accountingPackageService.get(accountingPackageId).then((pkg) => {
      if (pkg.PackageType == 3) {
        this.showIntacctFields = true;
      } else {
        this.showIntacctFields = false;
        this.postExistingTransactionDto.IntacctJournalId = null;
      }
    });
  }

  checkForValidPackage(valid: boolean) {
    this.displayWarning = valid;
  }

  private displayMissingCreditDebitAccountWarnings(validationResult: DebitCreditAccountValidationResultDto) {
    if (validationResult.HasAccountingPackageMismatch) {
      this._notificationService.translateWarning('revenue.post-transactions.post-transactions-search.accounting-package-mismatch-warning');
    }
    if (validationResult.HasEmptyAccounts) {
      this._notificationService.translateWarning('revenue.post-transactions.post-transactions-search.missing-debit-credit-account-error');
    }
  }

  private displayPostingTransactionsNotification() {
    this._notificationService.translateSuccess('revenue.post-transactions.posting-transactions-message');
  }
}
